import { useRef, useState } from 'react';
import clsx from 'clsx';
import { PageContainer } from '@/components/containers/page-container';
import { useTranslation } from '@/contexts/i18n-context';
import { useEncounter } from '@/contexts/encounter-context';
import { useUpdateEncountersStatus } from '@/api/update-encounter-status';
import { useMediaQuery } from '@/hooks/use-media-query';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { VisitFormButtonRow } from '@/components/containers/visit-form-button-row';
import { PatientInfoSection } from '@/components/review/patient-info-section';
import { ConditionSection } from '@/components/review/condition-section';
import { PreferencesSection } from '@/components/review/preferences-section';
import { MedicalInfoSection } from '@/components/review/medical-info-section';
import { usePostSubmitNavigation } from '@/hooks/use-post-submit-navigation';
import { EncounterStatusIdsType } from '@/api/api.schemas';
import { useBranding } from '@/outlets/branding-outlet';
import { useVisitNavigation } from '@/contexts/visit-navigation-context';
import { useNavigate } from '@/util/react-router-dom';
import { useResubmitEncounter } from '@/api/resubmit-encounter';
import { Button } from '@/components/button';
import navButtonStyles from '@/components/navigation/nav-button.module.css';
import { ReviewResubmitStickyBar } from '@/components/review/review-resubmit-sticky-bar';
import { ReviewExitModal } from '@/components/review/review-exit-modal';
import styles from './review-page.module.css';
import { useGetPharmacyById } from '@/api/get-pharmacy-by-id';

const translationBase = 'visit.review';

export function ReviewPage() {
  const { t } = useTranslation();
  const { encounter, invalidateEncounter } = useEncounter();
  const { mutate: updateEncounterStatus, isPending: isUpdatingEncounterStatus } =
    useUpdateEncountersStatus();
  const { mutate: resubmitEncounter, isPending: isResubmittingEncounter } = useResubmitEncounter();
  const { navigatePostSubmit } = usePostSubmitNavigation();
  const isMobile = useMediaQuery('--screen-md');
  const { hasIdVerification } = useBranding();
  const { nextStep } = useVisitNavigation();
  const navigate = useNavigate();
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const pharmacySectionRef = useRef<HTMLDivElement>(null);

  const { isFetching: isFetchingPharmacy, error: errorPharmacy } = useGetPharmacyById(
    encounter.preferredPharmacyId ?? 0,
    {
      query: { enabled: !!encounter.preferredPharmacyId },
    },
  );

  const hasAlreadySubmittedBefore = encounter.hasBeenSubmitted;
  const hasPendingChanges =
    hasAlreadySubmittedBefore && encounter.statusId !== EncounterStatusIdsType.InWaitingRoom;
  const isSubmitting = isUpdatingEncounterStatus || isResubmittingEncounter;

  const focusPharmacyError = () => {
    if (pharmacySectionRef.current) {
      pharmacySectionRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
      pharmacySectionRef.current.focus();
    }
  };

  const ensurePharmacyReady = () => {
    if (!!encounter.preferredPharmacyId && !isFetchingPharmacy && !errorPharmacy) {
      return true;
    }

    focusPharmacyError();
    return false;
  };

  const handleResubmit = () => {
    if (!ensurePharmacyReady()) {
      return;
    }

    resubmitEncounter(
      { encounterId: encounter.id },
      {
        onSuccess: () => {
          invalidateEncounter();
          navigate('/');
        },
      },
    );
  };

  const handleNext = () => {
    if (hasAlreadySubmittedBefore) {
      if (!hasPendingChanges) {
        navigate('/');
        return;
      }

      handleResubmit();
      return;
    }

    if (!ensurePharmacyReady()) {
      return;
    }

    updateEncounterStatus(
      {
        encounterId: encounter.id,
        data: {
          statusId: hasIdVerification
            ? EncounterStatusIdsType.PreSubmitIdentityVerification
            : EncounterStatusIdsType.PreSubmitPaymentInfo,
        },
      },
      {
        onSuccess: () => {
          invalidateEncounter();
          navigatePostSubmit();
        },
      },
    );
  };

  const handleBackToDashboard = () => {
    if (hasPendingChanges) {
      setIsExitModalOpen(true);
      return;
    }

    navigate('/');
  };

  return (
    <PageContainer>
      {hasAlreadySubmittedBefore && !isMobile && (
        <BackToDashboardButton className={styles.backButton} onClick={handleBackToDashboard} />
      )}

      {!isMobile && !hasAlreadySubmittedBefore && <VisitTitleButtons />}
      <GradientFormTitle>
        {hasAlreadySubmittedBefore && isMobile && (
          <BackToDashboardButton onClick={handleBackToDashboard} />
        )}
        {isMobile && !hasAlreadySubmittedBefore && <VisitTitleButtons />}
        {!hasAlreadySubmittedBefore && <VisitNavigation />}
        <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        <p>
          {t(
            hasAlreadySubmittedBefore
              ? `${translationBase}.editDescription`
              : `${translationBase}.description`,
          )}
        </p>
        {isMobile && !hasAlreadySubmittedBefore && <SaveProgressButton />}
      </GradientFormTitle>

      <PatientInfoSection
        showEditButton={!hasAlreadySubmittedBefore}
        showPaymentSection={hasAlreadySubmittedBefore}
      />

      <ConditionSection />

      <PreferencesSection pharmacySectionRef={pharmacySectionRef} />

      <MedicalInfoSection showNotALiveVisitDisclaimer />

      {hasAlreadySubmittedBefore ? (
        hasPendingChanges ? (
          <ReviewResubmitStickyBar onResubmit={handleResubmit} loading={isSubmitting} />
        ) : (
          <VisitFormButtonRow
            buttonTextKey={`${translationBase}.backToDashboard`}
            onClick={handleBackToDashboard}
          />
        )
      ) : (
        <VisitFormButtonRow
          buttonTextKey={`${translationBase}.continue`}
          nextStep={nextStep?.name}
          onClick={handleNext}
          loading={isSubmitting}
        />
      )}

      <ReviewExitModal
        isOpen={isExitModalOpen}
        onClose={() => setIsExitModalOpen(false)}
        onContinueEditing={() => setIsExitModalOpen(false)}
        onResubmit={handleResubmit}
        isResubmitting={isSubmitting}
      />
    </PageContainer>
  );
}

interface BackToDashboardButtonProps {
  onClick: () => void;
  className?: string;
}

function BackToDashboardButton({ onClick, className }: BackToDashboardButtonProps) {
  const { t } = useTranslation();

  return (
    <Button
      className={clsx(navButtonStyles.navButton, className)}
      variant="link"
      leftIcon="arrow_back"
      onClick={onClick}
    >
      {t(`${translationBase}.backToDashboard`)}
    </Button>
  );
}
